/**
 * theme.js — runtime light/dark switching.
 *
 * The colour values live in styles/theme.css as CSS variables; this module
 * only flips which set is active by toggling `data-theme` on <html>, and
 * persists the choice per-user (it's a personal preference, so it goes
 * through userScopedStorage like the rest of our per-account state).
 */
import { getUserScopedString, setUserScopedString, getUserScopedJSON, setUserScopedJSON } from './userScopedStorage';

const THEME_KEY = 'pidly.theme';
const OVERRIDES_KEY = 'pidly.themeOverrides';
// Set the first time the user PICKS a theme (the tuner / Account Settings call
// setStoredTheme). Until then the per-area defaults below apply. Starts with
// 'pidly.theme' so preferencesSync carries it across devices.
// Real data-theme values (each has a :root[data-theme='…'] block). 'auto' is
// NOT here — it's a meta-choice that resolves to light/dark from the OS.
// (noir was removed 2026-08-31 — it had become identical to dark; a stored
// 'noir' choice falls back to 'dark' through the VALID_CHOICES check.)
// 'test' is an experiment slot: identical to Dark (it shares Dark's token
// block in theme.css) but page CSS keyed on [data-theme='test'] strips the
// widget borders on the Overview and project pages — pick it to eyeball the
// borderless look, drop back to Dark to compare.
export const THEMES = ['dark', 'graphite', 'light', 'grey', 'sepia', 'slate', 'nord', 'midnight', 'amoled', 'high-contrast', 'test', 'custom', 'light-band', 'dark-band'];

/**
 * The BAND themes are an ordinary palette plus pidly's navy across the home
 * pages' top row and left rail (the website's own band). They are not a
 * palette of their own: `light-band` IS Light, `dark-band` IS Dark, with
 * `data-band` set on <html> — so every rule in the app keyed on
 * data-theme='light' / 'dark' keeps working untouched, and the band is one
 * block of CSS keyed on the flag.
 */
export const BAND_THEMES = { 'light-band': 'light', 'dark-band': 'dark' };
/**
 * How much of the frame a band theme colours, written to `data-band`: the
 * top row only ('top', 2026-10-07: the old full band, which also coloured
 * the side rails and the status strip, is gone; the user chose the top-row
 * one, which was briefly "Band 2").
 */
export const BAND_KIND = { 'light-band': 'top', 'dark-band': 'top' };
// Choices stored while "Band 2" existed read as the band they became.
const RETIRED = { 'light-band-2': 'light-band', 'dark-band-2': 'dark-band' };
/** The palette a choice paints in (a band theme wears its base's). */
export const baseThemeOf = (t) => BAND_THEMES[t] || t;

// Everything the user can pick/store (adds the system-following 'auto').
const VALID_CHOICES = ['auto', ...THEMES];

// Preset metadata for the Theme dialog — display order and a 3-swatch preview
// (app backdrop, panel, accent) so each card shows what it looks like without
// applying it. 'auto' follows the OS (its split swatch hints light↔dark);
// 'custom' is the user's own slot (base clones Dark) with a live preview.
export const THEME_META = [
  { id: 'auto', label: 'Auto', preview: ['#eef1f5', '#121111', '#3498db'] },
  { id: 'light', label: 'Light', preview: ['#eef1f5', '#ffffff', '#2f86c4'] },
  // Light and Dark with pidly's navy band across the home pages' top row
  // and left rail — the website's own.
  { id: 'light-band', label: 'Light Band', preview: ['#101d28', '#ffffff', '#2f86c4'] },
  { id: 'sepia', label: 'Sepia', preview: ['#efe7d8', '#fbf6ec', '#a8702e'] },
  { id: 'grey', label: 'Grey', preview: ['#2e3033', '#34373b', '#3a9ae0'] },
  { id: 'slate', label: 'Slate', preview: ['#2b313a', '#323a45', '#4a9fd6'] },
  { id: 'nord', label: 'Nord', preview: ['#2e3440', '#3b4252', '#88c0d0'] },
  { id: 'dark', label: 'Dark', preview: ['#121111', '#1a1a1a', '#ffffff'] },
  { id: 'dark-band', label: 'Dark Band', preview: ['#101d28', '#1a1a1a', '#ffffff'] },
  { id: 'graphite', label: 'Graphite', preview: ['#202020', '#2b2b2b', '#3498db'] },
  { id: 'midnight', label: 'Midnight', preview: ['#0f1420', '#161d2e', '#4aa8ff'] },
  { id: 'amoled', label: 'AMOLED', preview: ['#000000', '#000000', '#3498db'] },
  { id: 'high-contrast', label: 'High Contrast', preview: ['#000000', '#1a1a1a', '#4db8ff'] },
  { id: 'test', label: 'Test', preview: ['#121111', '#1a1a1a', '#ffffff'] },
  { id: 'custom', label: 'Custom', preview: ['#121111', '#1a1a1a', '#3498db'] },
];

// Document-area backdrop (the colour framing the PDF page in MainView /
// InfiniteView) per theme. Selecting a theme sets this once; the user can
// still change it afterwards and it persists as before. 'custom' derives from
// the custom theme's --surface-app at apply time.
export const THEME_DOC_BG = {
  light: '#d9dde4',
  sepia: '#e3d8c2',
  grey: '#3a3d42',
  slate: '#353c46',
  nord: '#2b303b',
  dark: '#2b2b2b',
  graphite: '#262626',
  midnight: '#0c1018',
  amoled: '#000000',
  'high-contrast': '#000000',
  test: '#2b2b2b',
  custom: '#2b2b2b',
};

// User-tunable tokens, grouped BY UI AREA for the customiser. Only hex tokens
// are listed (they drive native colour inputs); the rgba border/accent tints
// stay fixed.
//
// Each entry: [cssVar, label, hint]. The HINT names something the user can
// actually point at on screen — "the strip across the very top" beats "the
// toolbar surface token". A label alone told you what a colour was called,
// never where it lands, which is what made the customiser guesswork.
export const TUNABLE_TOKENS = [
  { group: 'Top bar', tokens: [
    ['--surface-toolbar', 'Top bar', 'The strip across the very top, behind the pidly name.'],
  ] },
  { group: 'Panels & surfaces', tokens: [
    ['--surface-app', 'App background', 'The deepest layer, behind everything else.'],
    ['--surface-panel', 'Panels & sidebars', 'The docks down either side, and dialogs like this one.'],
    ['--surface-raised', 'Cards', 'Anything that sits on top of a panel — a card, a thumbnail.'],
    ['--surface-control', 'Buttons', 'Ordinary buttons and control chips.'],
    ['--surface-hover', 'Hover', 'What a row or button turns when the pointer is over it.'],
    ['--surface-input', 'Inputs', 'Text fields, search boxes and dropdowns.'],
  ] },
  { group: 'Text', tokens: [
    ['--text-primary', 'Headings', 'Titles and anything that has to be read first.'],
    ['--text-secondary', 'Body text', 'Ordinary reading text.'],
    ['--text-muted', 'Metadata', 'Dates, counts, "3h ago" — present but quieter.'],
    ['--text-faint', 'Faint', 'Captions, disabled things, the quietest text there is.'],
  ] },
  { group: 'Accent', tokens: [
    ['--accent', 'Accent', 'Primary buttons, the active item, focus rings.'],
    ['--accent-hover', 'Accent hover', 'What the accent turns under the pointer.'],
    ['--accent-text', 'Accent text', 'Links and accent-coloured labels.'],
  ] },
  { group: 'Borders & status', tokens: [
    ['--border-strong', 'Borders', 'The lines that separate panels and cards.'],
    ['--danger', 'Danger', 'Errors, overdue, delete.'],
    ['--success', 'Success', 'Someone is here now, something worked.'],
    ['--warning', 'Warning', 'Due today, needs a look.'],
  ] },
];

const ALL_TUNABLE_VARS = TUNABLE_TOKENS.flatMap(g => g.tokens.map(([v]) => v));

// Every themeable token (superset of the hex-tunable ones). Used to fully clear
// inline overrides on theme switch — so a Custom theme that overrides the rgba
// border / accent-tint tokens doesn't leak those into the next theme — and to
// clone a theme into Custom faithfully (borders, tints, shadows and all).
const ALL_TOKEN_VARS = [
  '--surface-app', '--surface-toolbar', '--surface-input', '--surface-panel',
  '--surface-raised', '--surface-control', '--surface-hover',
  '--border-subtle', '--border-default', '--border-strong',
  '--text-primary', '--text-secondary', '--text-muted', '--text-faint',
  '--text-on-accent', '--toolbar-icon',
  '--accent', '--accent-hover', '--accent-text', '--accent-soft', '--accent-faint',
  '--danger', '--danger-hover', '--success', '--success-hover', '--warning',
  '--overlay-scrim', '--shadow-panel',
];

/**
 * The theme anyone who has never picked one gets, everywhere in the app
 * (2026-09-20): Light Band — white pages under pidly's navy top row and left
 * rail, the same band the website wears. It was Dark, with Light pinned on the
 * home pages only, so the app changed colour as you walked into a drawing.
 */
export const DEFAULT_THEME = 'light-band';

/** Read the stored theme CHOICE for the current user (may be 'auto'). */
export function getStoredTheme() {
  const raw = getUserScopedString(THEME_KEY, DEFAULT_THEME);
  const t = RETIRED[raw] || raw;
  return VALID_CHOICES.includes(t) ? t : DEFAULT_THEME;
}

/** True if the OS currently prefers a dark colour scheme. */
function systemPrefersDark() {
  try {
    return typeof window !== 'undefined' && !!window.matchMedia
      && window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false; // our default is light (see DEFAULT_THEME)
  }
}

/** Resolve a stored choice to a concrete data-theme ('auto' → system light/dark). */
export function resolveTheme(choice) {
  if (RETIRED[choice]) choice = RETIRED[choice];
  if (choice === 'auto') return systemPrefersDark() ? 'dark' : 'light';
  return THEMES.includes(choice) ? choice : DEFAULT_THEME;
}

/** The concrete data-theme currently on <html> (resolves 'auto'). */
export function getActiveTheme() {
  if (typeof document !== 'undefined' && document.documentElement.dataset.theme) {
    const base = document.documentElement.dataset.theme;
    if (document.documentElement.dataset.band) return base === 'dark' ? 'dark-band' : 'light-band';
    return base;
  }
  return resolveTheme(getStoredTheme());
}

/**
 * Apply a stored choice to the document (incl. user overrides for the resolved
 * theme) WITHOUT persisting it. Returns the resolved concrete theme.
 */
export function applyTheme(choice) {
  const resolved = resolveTheme(choice);
  if (typeof document !== 'undefined') {
    const base = baseThemeOf(resolved);
    document.documentElement.dataset.theme = base;
    // The band rides as its own flag, so the palette attribute stays a
    // value every existing rule already understands.
    if (BAND_KIND[resolved]) document.documentElement.dataset.band = BAND_KIND[resolved];
    else delete document.documentElement.dataset.band;
    applyOverrides(base);
  }
  return resolved;
}

// Watch the OS colour scheme; while the user is on 'auto', a system flip
// re-resolves the theme and re-seeds the document backdrop. Subscribed once.
let _systemSubscribed = false;
function subscribeSystemTheme() {
  if (_systemSubscribed || typeof window === 'undefined' || !window.matchMedia) return;
  _systemSubscribed = true;
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => {
    // Re-resolve the current page's choice; it may be 'auto' and follow the OS.
    const effective = themeChoiceForPath(window.location.pathname);
    if (effective !== 'auto') return;
    const resolved = applyTheme('auto');
    try {
      window.dispatchEvent(new CustomEvent('pidly:theme-changed',
        { detail: { theme: resolved, docBg: THEME_DOC_BG[resolved] } }));
    } catch { /* no-op */ }
  };
  try { mq.addEventListener('change', onChange); } catch { mq.addListener?.(onChange); }
}

// ── User colour overrides ─────────────────────────────────────────────
// Stored per-user as { dark: { '--token': '#hex', … }, light: { … } } and
// applied as inline custom properties on <html>, which win over theme.css.

function getAllOverrides() {
  return getUserScopedJSON(OVERRIDES_KEY, {}) || {};
}

/** The override map for one theme ({} if none). */
export function getThemeOverrides(theme) {
  return getAllOverrides()[theme] || {};
}

/** Re-apply inline overrides for a theme: clear every token var, then set stored. */
export function applyOverrides(theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  ALL_TOKEN_VARS.forEach(v => root.style.removeProperty(v));
  const ov = getThemeOverrides(theme);
  Object.entries(ov).forEach(([k, val]) => root.style.setProperty(k, val));
}

/**
 * Clone the currently-displayed theme into the user's Custom slot: snapshot
 * every token's computed value into overrides.custom so 'custom' starts as a
 * faithful copy of whatever's on screen (the user then tweaks it freely).
 */
export function duplicateToCustom() {
  if (typeof document === 'undefined') return;
  const cs = getComputedStyle(document.documentElement);
  const vals = {};
  ALL_TOKEN_VARS.forEach(v => {
    const raw = cs.getPropertyValue(v).trim();
    if (raw) vals[v] = raw;
  });
  const all = getAllOverrides();
  all.custom = vals;
  setUserScopedJSON(OVERRIDES_KEY, all);
}

/** Set one token override for a theme; live-applies if that theme is active. */
export function setThemeOverride(theme, token, value) {
  const all = getAllOverrides();
  all[theme] = { ...(all[theme] || {}), [token]: value };
  setUserScopedJSON(OVERRIDES_KEY, all);
  if (getStoredTheme() === theme && typeof document !== 'undefined') {
    document.documentElement.style.setProperty(token, value);
  }
}

/** Clear all overrides for a theme (revert to theme.css defaults). */
export function resetThemeOverrides(theme) {
  const all = getAllOverrides();
  delete all[theme];
  setUserScopedJSON(OVERRIDES_KEY, all);
  if (getStoredTheme() === theme) applyOverrides(theme);
}

// ── One theme everywhere ──────────────────────────────────────────────
// pidly has ONE theme for every page (2026-10-03). Per-area themes (home vs
// workspace vs data pages) and the area defaults are gone; any old
// pidly.themePages / pidly.themeChosen keys are simply not read.
// The sign-in pages (sign in, verify email, reset password, invite) are
// always Light: nobody is signed in yet, so there is no choice to follow.
const AUTH_PATH = /^\/(login|verify|confirm-email|reset-password|join)(\/|$)/;

/** The effective CHOICE for a pathname: Light on the sign-in pages, else the one theme. */
export function themeChoiceForPath(pathname) {
  if (AUTH_PATH.test(pathname || '')) return 'light';
  return getStoredTheme();
}

/**
 * Apply the effective theme for a pathname (the route-change entry point —
 * App's PageThemeApplier calls this on every navigation). notify fires the
 * doc-backdrop event, used when a CHANGE of preference should re-seed it.
 */
export function applyThemeForPath(pathname, { notify = false } = {}) {
  const resolved = applyTheme(themeChoiceForPath(pathname));
  if (notify && typeof window !== 'undefined') {
    let docBg = THEME_DOC_BG[resolved];
    if (resolved === 'custom' && typeof document !== 'undefined') {
      try { docBg = getComputedStyle(document.documentElement).getPropertyValue('--surface-app').trim() || docBg; } catch { /* no-op */ }
    }
    try { window.dispatchEvent(new CustomEvent('pidly:theme-changed', { detail: { theme: resolved, docBg } })); } catch { /* no-op */ }
  }
  return resolved;
}

/** The backdrop the CURRENT theme would give the document (what a theme
 *  pick re-seeds the viewer with, when the person has not chosen one). */
export function currentThemeDocBg() {
  const resolved = resolveTheme(typeof window !== 'undefined' ? themeChoiceForPath(window.location.pathname) : getStoredTheme());
  let docBg = THEME_DOC_BG[resolved];
  if (resolved === 'custom' && typeof document !== 'undefined') {
    try { docBg = getComputedStyle(document.documentElement).getPropertyValue('--surface-app').trim() || docBg; } catch { /* no-op */ }
  }
  return docBg || null;
}

/** Persist a theme CHOICE (may be 'auto') + apply it for the current user. */
export function setStoredTheme(choice) {
  const c = VALID_CHOICES.includes(choice) ? choice : DEFAULT_THEME;
  setUserScopedString(THEME_KEY, c);
  // Apply where the user is standing (the sign-in pages keep their own look).
  const resolved = (typeof window !== 'undefined')
    ? applyThemeForPath(window.location.pathname)
    : applyTheme(c);
  // Notify the viewer so the document backdrop can switch to match. Fired only
  // on explicit selection (not boot) — so a user's later manual backdrop tweak
  // still survives reloads; picking a theme just re-seeds it.
  if (typeof window !== 'undefined') {
    let docBg = THEME_DOC_BG[resolved];
    if (resolved === 'custom' && typeof document !== 'undefined') {
      try { docBg = getComputedStyle(document.documentElement).getPropertyValue('--surface-app').trim() || docBg; } catch { /* no-op */ }
    }
    try { window.dispatchEvent(new CustomEvent('pidly:theme-changed', { detail: { theme: resolved, docBg } })); } catch { /* no-op */ }
  }
  return c; // the stored choice (so the UI highlights e.g. the Auto card)
}

/** Apply whatever is stored for the current user. Call on boot and after login. */
export function initTheme() {
  subscribeSystemTheme();
  // The bold-borders experiment is over: the projects page is always bold
  // (theme.css scopes the strong tokens to it); nothing else is. Clear the
  // old root class a browser may still carry.
  if (typeof document !== 'undefined') document.documentElement.classList.remove('pidly-bold-borders');
  // The sign-in pages keep their own look; everything else gets the one theme.
  if (typeof window !== 'undefined') return applyThemeForPath(window.location.pathname);
  return applyTheme(getStoredTheme());
}
